import { MatDialog } from '@angular/material/dialog';
import { ChangelogDialogComponent, ChangelogDialogConfig } from '../../shared-components/changelog-dialog/changelog-dialog.component';
import { Component, OnInit, OnDestroy, Inject } from '@angular/core';
import { DefaultValuesService } from 'src/app/services/defaultvalues.service';
import { InvestmentControllingService } from 'src/app/services/investment-controlling.service';
import { IncoActualMeasures } from 'src/app/interfaces/investmentcontrolling/inco-actual-measures';
import { IncoMeasureUpdateRequest } from 'src/app/interfaces/investmentcontrolling/inco-measure-update';
import { IncoActualReportingResponse } from 'src/app/interfaces/investmentcontrolling/inco-actual-reporting-response';
import { IncoPeriodsInfo } from 'src/app/interfaces/investmentcontrolling/inco-periods-info';
import { ToastrService } from 'ngx-toastr';
import { DialogService } from 'src/app/services/dialog.service';
import { TableColumn } from '../../shared-components/data-table/data-table.component';
import { HttpClient } from '@angular/common/http';
import { Utils } from '../../../../utils/utils';
import { Subject, merge } from 'rxjs';
import { takeUntil, filter, startWith, debounceTime } from 'rxjs/operators';
import { Router } from '@angular/router';
import { isPhaseEditableForPlanning, isPhaseEditableForExecution } from 'src/app/constants/inco-phases';
import { TableUtils } from 'src/app/utils/table-utils';

@Component({
  selector: 'app-inco-actualreporting',
  templateUrl: './inco-actualreporting.component.html',
  styleUrls: ['./inco-actualreporting.component.less', '../../../../style/new-generic-styles.component.less'],
  standalone: false
})
export class IncoActualreportingComponent implements OnInit, OnDestroy {
  isExcelModeEnabled: boolean = false;
  title: string = '';
  isLoading: boolean = false;
  isEditModeEnabled: boolean = false;
  isEditAllowedForCurrentQuarter: boolean = true;

  projects: IncoActualMeasures[] = [];
  tableData: any[] = [];
  originalProjects: IncoActualMeasures[] = [];
  updatedMeasures: Map<number, number | null> = new Map();

  updatedForecastMeasures: Map<string, { projectId: number; value: number | null; forecastType: 'FC' | 'P1' | 'P2' }> = new Map();

  periods: IncoPeriodsInfo | null = null;

  sapCurrentTotal: number = 0;
  sapFcTotal: number = 0;
  sapP1Total: number = 0;
  sapP2Total: number = 0;

  currencyShort: string = 'EUR';
  currencySymbol: string = '€';
  decimalDigits: number = 2;

  verificationStatus: 'success' | 'error' | '' = '';
  isVerified: boolean = false;

  columnValidationStatus: Map<string, 'valid' | 'invalid' | null> = new Map();

  tableColumns: TableColumn[] = [];

  isRowEditableFn = (row: any, _rowIndex: number): boolean => {
    if (this.defaults.isMersyAdminINCO) {
      return true;
    }
    return isPhaseEditableForPlanning(row.phaseId);
  };

  isCellEditableForYtdFn = (row: any, _rowIndex: number): boolean => {
    if (this.defaults.isMersyAdminINCO) {
      return true;
    }
    return isPhaseEditableForExecution(row.phaseId);
  };

  isCellEditableForForecastFn = (row: any, _rowIndex: number): boolean => {
    if (this.defaults.isMersyAdminINCO) {
      return true;
    }
    return isPhaseEditableForPlanning(row.phaseId);
  };

  private quarterToPeriod(quarter: number): number {
    return quarter * 3;
  }

  private readonly destroy$ = new Subject<void>();

  constructor(
    public defaults: DefaultValuesService,
    private readonly incoService: InvestmentControllingService,
    private readonly toastrService: ToastrService,
    private readonly dialog: DialogService,
    private readonly http: HttpClient,
    private readonly router: Router,
    @Inject('BASE_URL') private readonly baseUrl: string,
    private readonly matDialog: MatDialog
  ) {
    this.initializeColumns();
  }

  ngOnInit(): void {
    this.updateTitle();
    this.setupSubscriptions();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupSubscriptions(): void {
    merge(
      this.defaults.managementIdMessageChanges$,
      this.defaults.fiscalYearMessageChanges$,
      this.defaults.quarter$
    ).pipe(
      startWith(null),
      debounceTime(50),
      filter(() => this.isCurrentManagementIdValidForInco()),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.isEditModeEnabled = false;
      this.isVerified = false;
      this.verificationStatus = '';
      this.loadData();
    });
  }

  private isCurrentManagementIdValidForInco(): boolean {
    const currentNode = this.findNodeByValue(this.defaults.managementids, this.defaults.managementid);
    return currentNode?.BusinessLevel === 'MANAGEMENT' || currentNode?.BusinessLevel === 'FIELDBRANCH';
  }

  private findNodeByValue(nodes: any[], value: string): any | null {
    for (const node of nodes) {
      if (node.Value === value) return node;
      if (node.Groups?.length > 0) {
        const found = this.findNodeByValue(node.Groups, value);
        if (found) return found;
      }
    }
    return null;
  }

  viewChangeLog(row: any): void {
    const actualReportingId = row.id || row.projectId;
    if (!actualReportingId) return;

    const columns: TableColumn[] = [
      {
        key: 'historyChangedAtUtc',
        header: 'Date',
        formatter: (value) => TableUtils.formatDate(value),
        align: 'left',
      },
      { key: 'historyChangedByUser', header: 'User', align: 'left', },
      { 
        key: 'isCurrent', 
        header: 'Status',
        align: 'left',
        formatter: (value) => value ? 'Current' : 'Archived',
      },
      { 
        key: 'value', 
        header: 'Value', 
        type: 'currency',
        align: 'right',
        formatter: (value) => TableUtils.formatCurrency(value),
      },
      { key: 'fiscalYear', header: 'Fiscal Year', align: 'left', },
      {
        key: 'projectVersion',
        header: 'Version',
        align: 'left',
        formatter: (_value, row) => {
          if (row.fiscalYear === undefined || !row.projectVersion) return _value;
          const inputFiscalYear = this.defaults.fiscalYear;
          const fiscalYear = row.fiscalYear;
          const projectVersion = row.projectVersion;
          if (projectVersion === 'ACTUAL' && fiscalYear === inputFiscalYear) {
            return 'ACT YTD';
          } else if (projectVersion === 'ACT-FC' && fiscalYear === inputFiscalYear) {
            return 'FC CFY';
          } else if (fiscalYear === inputFiscalYear + 1) {
            return 'FC P1';
          } else if (fiscalYear === inputFiscalYear + 2) {
            return 'FC P2';
          } else {
            return _value;
          }
        }
      },
    ];

    const fiscalYear = this.defaults.fiscalYear;
    const currentQuarter = this.defaults.quarterId;
    if (!currentQuarter) {
      this.toastrService.error('Quarter is not selected. Cannot view history.');
      return;
    }
    const period = this.quarterToPeriod(currentQuarter);

    const config: ChangelogDialogConfig = {
      title: `Actual Reporting History: ${row.id}`,
      apiEndpoint: `api/inco/actualreporting/${actualReportingId}/history?fiscalYear=${fiscalYear}&period=${period}`,
      columns: columns,
      backButtonText: 'Back'
    };

    this.matDialog.open(ChangelogDialogComponent, {
      width: '95vw',
      data: config
    });
  }

  private initializeColumns(): void {
    const prevYearLabel = this.periods?.previousYear?.label || '-';
    const prevQuarterLabel = this.periods?.previousQuarter?.label || '-';
    const currentLabel = this.periods?.current?.label || '-';

    const prevYearHeaderMain = TableUtils.getLabelWithoutYear(prevYearLabel);
    const prevYearHeaderSub = TableUtils.getYearPart(prevYearLabel);

    const prevQuarterHeaderMain = TableUtils.getLabelWithoutYear(prevQuarterLabel);
    const prevQuarterHeaderSub = TableUtils.getYearPart(prevQuarterLabel);

    const currentHeaderMain = TableUtils.getLabelWithoutYear(currentLabel);
    const currentHeaderSub = TableUtils.getYearPart(currentLabel);

    const fcYearLabel = TableUtils.getYearPart(this.periods?.fc?.label || '');
    const p1YearLabel = TableUtils.getYearPart(this.periods?.p1?.label || '');
    const p2YearLabel = TableUtils.getYearPart(this.periods?.p2?.label || '');

    const baseColumns: TableColumn[] = [
      {
        key: 'projectId',
        header: 'Project ID',
        width: '5%',
        align: 'left',
        editable: false,
        formatter: (value, row, index) => index === 0 ? 'TOTAL' : value?.toString() || '-'
      },
      {
        key: 'projectTitle',
        header: 'Project Title',
        width: '10%',
        align: 'left',
        editable: false,
        formatter: (value, _row, index) => index === 0 ? '-' : value || '-'
      },
      {
        key: 'maintenance',
        header: 'Maintenance',
        headerSub: '%',
        width: '4%',
        align: 'center',
        editable: false,
        formatter: (value, _row, index) => index === 0 ? '-' : TableUtils.formatDecimal(value, TableUtils.percentDecimals)
      },
      {
        key: 'expansion',
        header: 'Expansion',
        headerSub: '%',
        width: '4%',
        align: 'center',
        editable: false,
        formatter: (value, _row, index) => index === 0 ? '-' : TableUtils.formatDecimal(value, TableUtils.percentDecimals)
      },
      {
        key: 'phase',
        header: 'Phase',
        width: '4%',
        align: 'left',
        editable: false,
        formatter: (value, _row, index) => index === 0 ? '-' : value || '-'
      },
      {
        key: 'currency',
        header: 'Currency',
        width: '1%',
        align: 'left',
        editable: false,
        formatter: (_value, _row, _index) => this.currencyShort || '-'
      },
      {
        key: 'previousYearValue',
        header: prevYearHeaderMain || prevYearLabel,
        headerSub: prevYearHeaderSub,
        width: '7%',
        align: 'right',
        type: 'currency',
        editable: false,
        formatter: (value, _row, index) => {
          if (index === 0) {
            return TableUtils.formatCurrency(this.getTotalPreviousYearValue());
          }
          return TableUtils.formatCurrency(value);
        },
        cellClass: (_value, _row, index) => {
          if (index !== undefined && index > 0) {
            return 'readonly-cell';
          }
          return null;
        }
      },
      {
        key: 'previousQuarterValue',
        header: prevQuarterHeaderMain || prevQuarterLabel,
        headerSub: prevQuarterHeaderSub,
        width: '7%',
        align: 'right',
        type: 'currency',
        editable: false,
        formatter: (value, _row, index) => {
          if (index === 0) {
            return TableUtils.formatCurrency(this.getTotalPreviousQuarterValue());
          }
          return TableUtils.formatCurrency(value);
        },
        cellClass: (_value, _row, index) => {
          if (index !== undefined && index > 0) {
            return 'readonly-cell';
          }
          return null;
        }
      },
      {
        key: 'currentValue',
        header: currentHeaderMain || currentLabel,
        headerSub: currentHeaderSub,
        width: '7%',
        align: 'right',
        type: 'currency',
        editable: true,
        isCellEditableFn: this.isCellEditableForYtdFn,
        formatter: (value, _row, index) => {
          if (index === 0) {
            return TableUtils.formatCurrency(this.getTotalCurrentValue());
          }
          return TableUtils.formatCurrency(value);
        },
        cellClass: (_value, _row, index) => {
          if (index === 0 && this.columnValidationStatus.has('currentValue')) {
            const status = this.columnValidationStatus.get('currentValue');
            return TableUtils.getValidationClass(status);
          }
          return null;
        }
      },
      {
        key: 'sapValue',
        header: `${currentHeaderMain || currentLabel} SAP`,
        headerSub: currentHeaderSub,
        width: '7%',
        align: 'right',
        editable: false,
        formatter: (_value, _row, index) => {
          if (index === 0) {
            return TableUtils.formatCurrency(this.sapCurrentTotal);
          }
          return '-';
        },
        cellClass: (_value, _row, index) => {
          if (index !== undefined && index > 0) {
            return 'readonly-cell';
          }
          return null;
        }
      }
    ];

    const forecastColumns: TableColumn[] = [
      {
        key: 'fcValue',
        header: 'FC',
        headerSub: fcYearLabel,
        width: '7%',
        align: 'right',
        type: 'currency',
        editable: true,
        isCellEditableFn: this.isCellEditableForForecastFn,
        formatter: (value, _row, index) => {
          if (index === 0) {
            return TableUtils.formatCurrency(this.getTotalFcValue());
          }
          return TableUtils.formatCurrency(value);
        },
        cellClass: (_value, _row, index) => {
          if (index === 0 && this.columnValidationStatus.has('fcValue')) {
            const status = this.columnValidationStatus.get('fcValue');
            return TableUtils.getValidationClass(status);
          }
          return null;
        }
      },
      {
        key: 'fcSapValue',
        header: 'BUD SAP',
        headerSub: fcYearLabel,
        width: '7%',
        align: 'right',
        type: 'currency',
        editable: false,
        formatter: (_value, _row, index) => {
          if (index === 0) {
            return TableUtils.formatCurrency(this.sapFcTotal);
          }
          return '-';
        },
        cellClass: (_value, _row, index) => {
          if (index !== undefined && index > 0) {
            return 'readonly-cell';
          }
          return null;
        }
      },
      {
        key: 'p1Value',
        header: 'PL1',
        headerSub: p1YearLabel,
        width: '7%',
        align: 'right',
        type: 'currency',
        editable: true,
        isCellEditableFn: this.isCellEditableForForecastFn,
        formatter: (value, _row, index) => {
          if (index === 0) {
            return TableUtils.formatCurrency(this.getTotalP1Value());
          }
          return TableUtils.formatCurrency(value);
        },
        cellClass: (_value, _row, index) => {
          if (index === 0 && this.columnValidationStatus.has('p1Value')) {
            const status = this.columnValidationStatus.get('p1Value');
            return TableUtils.getValidationClass(status);
          }
          return null;
        }
      },
      {
        key: 'p1SapValue',
        header: 'PL1 SAP',
        headerSub: p1YearLabel,
        width: '7%',
        align: 'right',
        type: 'currency',
        editable: false,
        formatter: (_value, _row, index) => {
          if (index === 0) {
            return TableUtils.formatCurrency(this.sapP1Total);
          }
          return '-';
        },
        cellClass: (_value, _row, index) => {
          if (index !== undefined && index > 0) {
            return 'readonly-cell';
          }
          return null;
        }
      },
      {
        key: 'p2Value',
        header: 'PL2',
        headerSub: p2YearLabel,
        width: '7%',
        align: 'right',
        type: 'currency',
        editable: true,
        isCellEditableFn: this.isCellEditableForForecastFn,
        formatter: (value, _row, index) => {
          if (index === 0) {
            return TableUtils.formatCurrency(this.getTotalP2Value());
          }
          return TableUtils.formatCurrency(value);
        },
        cellClass: (_value, _row, index) => {
          if (index === 0 && this.columnValidationStatus.has('p2Value')) {
            const status = this.columnValidationStatus.get('p2Value');
            return TableUtils.getValidationClass(status);
          }
          return null;
        }
      },
      {
        key: 'p2SapValue',
        header: 'PL2 SAP',
        headerSub: p2YearLabel,
        width: '7%',
        align: 'right',
        type: 'currency',
        editable: false,
        formatter: (_value, _row, index) => {
          if (index === 0) {
            return TableUtils.formatCurrency(this.sapP2Total);
          }
          return '-';
        },
        cellClass: (_value, _row, index) => {
          if (index !== undefined && index > 0) {
            return 'readonly-cell';
          }
          return null;
        }
      }
    ];

    const historyColumn: TableColumn = {
      key: 'history',
      header: 'History',
      width: '4%',
      align: 'center',
      editable: false,
      formatter: (_value, row, index) => {
        if (index === 0) return '-';
        return row.id || row.projectId ? 'show' : '-';
      },
      cellClass: (_value, _row, index) => index === 0 ? 'total-row' : 'history-cell',
    };

    this.tableColumns = [...baseColumns, ...forecastColumns, historyColumn];
  }

  private updateTitle(): void {
    const periodLabel = this.periods?.current?.label || `Q${this.defaults.quarterId || 1}`;
    this.title = `INCO: Actual Reporting (${this.defaults.managementName} - ${periodLabel})`;
  }

  private lastLoadedParams: string = '';

  loadData(showLoading: boolean = true, force: boolean = false): void {
    const currentParams = `${this.defaults.managementid}-${this.defaults.fiscalYear}-${this.defaults.quarterId}`;
    if (!force && currentParams === this.lastLoadedParams) {
      return;
    }
    this.lastLoadedParams = currentParams;

    if (showLoading) {
      this.isLoading = true;
    }
    this.verificationStatus = '';

    this.incoService.loadActualProjects(
      this.defaults.managementid,
      this.defaults.fiscalYear,
      this.defaults.quarterId || 1,
      this,
      (tthis: IncoActualreportingComponent, result: IncoActualReportingResponse) => {
        tthis.periods = result.periods;
        tthis.projects = result.projects;
        tthis.sapCurrentTotal = result.sapCurrentTotal || 0;
        tthis.sapFcTotal = result.sapFcTotal || 0;
        tthis.sapP1Total = result.sapP1Total || 0;
        tthis.sapP2Total = result.sapP2Total || 0;
        tthis.currencyShort = result.currencyShort || 'EUR';
        tthis.currencySymbol = result.currencySymbol || '€';
        tthis.decimalDigits = 2;
        tthis.originalProjects = structuredClone(result.projects);
        tthis.updateEditPermission();
        tthis.initializeColumns(); 
        tthis.updateTitle();
        tthis.buildTableData();
        tthis.isLoading = false;
        tthis.updatedMeasures.clear();
        tthis.updatedForecastMeasures.clear();
        tthis.columnValidationStatus.clear();
      },
      (tthis: IncoActualreportingComponent, error: any) => {
        tthis.dialog.showErrorDialog(error);
        tthis.isLoading = false;
      }
    );
  }

  onClickEdit(): void {
    if (this.isEditModeEnabled) {
      this.projects = structuredClone(this.originalProjects);
      this.buildTableData();
      this.updatedMeasures.clear();
      this.updatedForecastMeasures.clear();
      this.isEditModeEnabled = false;
      this.isVerified = false;
      this.verificationStatus = '';
      this.columnValidationStatus.clear();
    } else {
      this.isEditModeEnabled = true;
      this.isVerified = false;
      this.verificationStatus = '';
      this.columnValidationStatus.clear();
    }
  }

  onClickSave(): void {
    if (this.updatedMeasures.size === 0 && this.updatedForecastMeasures.size === 0) {
      this.isEditModeEnabled = false;
      return;
    }

    const measures: IncoMeasureUpdateRequest[] = [];
    const currentQuarter = this.defaults.quarterId;
    if (!currentQuarter) {
      this.toastrService.error('Quarter is not selected. Please select a quarter before saving.');
      return;
    }
    const currentPeriod = this.quarterToPeriod(currentQuarter);
    const currentFiscalYear = this.defaults.fiscalYear;

    this.updatedMeasures.forEach((value, projectId) => {
      measures.push({
        projectId: projectId,
        value: value,
        mgmtId: this.defaults.managementid,
        fiscalYear: currentFiscalYear,
        period: currentPeriod,
        projectVersion: 'ACTUAL',
        inputFiscalYear: currentFiscalYear
      });
    });

    this.updatedForecastMeasures.forEach((data, _key) => {
      let targetFiscalYear: number;
      switch (data.forecastType) {
        case 'FC':
          targetFiscalYear = currentFiscalYear;
          break;
        case 'P1':
          targetFiscalYear = currentFiscalYear + 1;
          break;
        case 'P2':
          targetFiscalYear = currentFiscalYear + 2;
          break;
      }

      measures.push({
        projectId: data.projectId,
        value: data.value,
        mgmtId: this.defaults.managementid,
        fiscalYear: targetFiscalYear,
        period: currentPeriod,
        projectVersion: 'ACT-FC',
        inputFiscalYear: currentFiscalYear
      });
    });

    this.incoService.saveMeasures(
      measures,
      this,
      (tthis: IncoActualreportingComponent, _result: any) => {
        tthis.toastrService.success('Values saved successfully!');
        tthis.isEditModeEnabled = false;
        tthis.isVerified = false;
        tthis.verificationStatus = '';
        tthis.loadData(false, true);
      },
      (tthis: IncoActualreportingComponent, error: any) => {
        tthis.dialog.showErrorDialog(error);
      }
    );
  }

  onCellValueChange(event: { row: any; column: TableColumn; value: any; rowIndex: number }): void {
    if (event.rowIndex === 0) return; 
      
    const project = event.row as IncoActualMeasures;
    const originalProject = this.projects.find(p => p.id === project.id);
    if (!originalProject) return;

    if (event.column.key === 'currentValue') {
      this.handleActualValueChange(project.id, originalProject, event.value);
    } else {
      this.handleForecastValueChange(event.column.key, project.id, originalProject, event.value);
    }
      
    if (this.isVerified) {
      this.isVerified = false;
      this.verificationStatus = '';
      this.columnValidationStatus.clear();
      this.initializeColumns();
    }
  }

  private handleActualValueChange(projectId: number, originalProject: IncoActualMeasures, value: any): void {
    originalProject.currentValue = value;
    this.updatedMeasures.set(projectId, value);
  }

  private handleForecastValueChange(columnKey: string, projectId: number, originalProject: IncoActualMeasures, value: any): void {

    const forecastMap: Record<string, { prop: keyof IncoActualMeasures; type: 'FC' | 'P1' | 'P2' }> = {
      fcValue: { prop: 'fcValue', type: 'FC' },
      p1Value: { prop: 'p1Value', type: 'P1' },
      p2Value: { prop: 'p2Value', type: 'P2' },
    };

    const config = forecastMap[columnKey];
    if (!config) {
      return;
    }

    (originalProject[config.prop] as number | null) = value;

    this.updatedForecastMeasures.set(
      `${projectId}_${config.type}`,
      { projectId, value, forecastType: config.type },
    );
  }

  private buildTableData(): void {
    const totalRow = {
      id: -1,
      projectId: 0,
      projectTitle: 'TOTAL',
      maintenance: null,
      expansion: null,
      phase: '-',
      previousYearValue: null,
      previousQuarterValue: null,
      currentValue: null,
      currentMeasureId: null,
      isTotal: true,
      fcValue: null,
      p1Value: null,
      p2Value: null
    };
    
    this.tableData = [totalRow, ...this.projects];
  }

  getTotalPreviousYearValue(): number {
    return this.projects.reduce((sum, p) => sum + (p.previousYearValue || 0), 0);
  }

  getTotalPreviousQuarterValue(): number {
    return this.projects.reduce((sum, p) => sum + (p.previousQuarterValue || 0), 0);
  }

  getTotalCurrentValue(): number {
    return this.projects.reduce((sum, p) => sum + (p.currentValue || 0), 0);
  }

  getTotalFcValue(): number {
    return this.projects.reduce((sum, p) => sum + (p.fcValue || 0), 0);
  }

  getTotalP1Value(): number {
    return this.projects.reduce((sum, p) => sum + (p.p1Value || 0), 0);
  }

  getTotalP2Value(): number {
    return this.projects.reduce((sum, p) => sum + (p.p2Value || 0), 0);
  }


  hasChanges(): boolean {
    return this.updatedMeasures.size > 0 || this.updatedForecastMeasures.size > 0;
  }

  onClickVerify(): void {
    const totalCurrent = this.getTotalCurrentValue();
    const sapCurrent = this.sapCurrentTotal;

    const currentMatch = TableUtils.amountsMatch(totalCurrent, sapCurrent);
    this.columnValidationStatus.set('currentValue', currentMatch ? 'valid' : 'invalid');

    this.columnValidationStatus.set('fcValue', null);
    this.columnValidationStatus.set('p1Value', null);
    this.columnValidationStatus.set('p2Value', null);

    if (currentMatch) {
      this.verificationStatus = 'success';
      this.isVerified = true;
      this.toastrService.success('The current quarter values match with SAP. You can now save.');
    } else {
      this.verificationStatus = 'error';
      this.isVerified = false;
      this.toastrService.warning('The current quarter values do not match with SAP. Please check the highlighted column.');
    }

    this.initializeColumns();
  }

  onClickExcel(): void {
    this.isExcelModeEnabled = true;
    this.isEditModeEnabled = false;
  }

  onClickDownload(): void {
    let date = new Date()
        const year = date.getFullYear().toString(); // a�o
        const month = (date.getMonth() + 1).toString().padStart(2, '0'); // mes con dos d�gitos
        const day = date.getDate().toString().padStart(2, '0'); // d�a con dos d�gitos

    Utils.httpDownloadIncoActualReporting(
      this.http,
      this.baseUrl,
      `${year}${month}${day}_${this.defaults.managementid}_${this.defaults.groupFunction}_${this.defaults.fiscalYear - 1}-${this.defaults.fiscalYear}-Q${this.defaults.quarterId!}_ACT`,
      this.defaults.managementid,
      this.defaults.fiscalYear,
      this.defaults.quarterId!,
      this,
      undefined,
      (tthis: any, error: any) => {
        this.toastrService.error('Error downloading file');
      }
    );
  }


  onClickUpload(): void {
    this.router.navigate(["file/" + Utils.getFileUploadIncoActualReportingInformation()]);
  }

  private updateEditPermission(): void {
    if (this.defaults.isMersyAdminINCO) {
      this.isEditAllowedForCurrentQuarter = true;
      return;
    }

    const currentQuarter = this.getCurrentQuarterFromDate();
    const selectedQuarter = this.defaults.quarterId || 1;
    this.isEditAllowedForCurrentQuarter = selectedQuarter >= currentQuarter;
  }

  private getCurrentQuarterFromDate(): number {
    const month = new Date().getMonth();
    if (month >= 9) return 1;
    if (month <= 2) return 2;
    if (month <= 5) return 3;
    return 4;
  }

  onClickExitExcel(): void {
    this.isExcelModeEnabled = false;
  }
}
